import { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Users, 
  LogOut, 
  CheckCircle, 
  AlertCircle, 
  Briefcase, 
  GraduationCap,
  BookOpen,
  DollarSign,
  Star,
  FileText,
  Edit,
  Loader2,
  ExternalLink,
  Building2,
  Award,
  BookMarked,
  CircleDot,
  Calendar,
  Megaphone,
  UserCircle,
  Wallet,
  LifeBuoy
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import ExpertOpportunities from '@/components/ExpertOpportunities';
import ExpertAvailableProjects from '@/components/ExpertAvailableProjects';
import FinanceSummary from '@/components/FinanceSummary';

// Helper component to render formatted list items
const FormattedList = ({ content, icon: Icon, emptyMessage }) => {
  if (!content || content.trim() === '') {
    return <p className="text-muted-foreground italic">{emptyMessage}</p>;
  }

  const items = content.split('\n').filter(item => item.trim() !== '');

  if (items.length === 0) {
    return <p className="text-muted-foreground italic">{emptyMessage}</p>;
  }

  if (items.length === 1) {
    return <p className="text-muted-foreground">{items[0]}</p>;
  }

  return (
    <ul className="space-y-3">
      {items.map((item, index) => (
        <li key={index} className="flex items-start gap-3">
          <div className="mt-1 shrink-0">
            {Icon ? (
              <Icon className="w-4 h-4 text-primary" />
            ) : (
              <CircleDot className="w-3 h-3 text-primary mt-0.5" />
            )}
          </div>
          <span className="text-muted-foreground">{item.trim()}</span>
        </li>
      ))}
    </ul>
  );
};

// Helper component for publication/work items
const PublicationList = ({ content, emptyMessage }) => {
  if (!content || content.trim() === '') {
    return <p className="text-muted-foreground italic">{emptyMessage}</p>;
  }

  const items = content.split('\n').filter(item => item.trim() !== '');

  if (items.length === 0) {
    return <p className="text-muted-foreground italic">{emptyMessage}</p>;
  }

  return (
    <div className="space-y-3">
      {items.map((item, index) => {
        const yearMatch = item.match(/\((\d{4})\)/);
        const typeMatch = item.match(/\[([^\]]+)\]$/);
        
        let title = item;
        let year = yearMatch ? yearMatch[1] : null;
        let type = typeMatch ? typeMatch[1] : null;
        
        if (typeMatch) {
          title = title.replace(/\s*\[[^\]]+\]$/, '');
        }

        return (
          <div key={index} className="p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                <FileText className="w-4 h-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-foreground leading-relaxed">{title}</p>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  {year && <Badge variant="secondary" className="text-xs">{year}</Badge>}
                  {type && <Badge variant="outline" className="text-xs capitalize">{type.replace(/-/g, ' ')}</Badge>}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Helper component for employment/education items
const ExperienceList = ({ content, icon: Icon, emptyMessage }) => {
  if (!content || content.trim() === '') {
    return <p className="text-muted-foreground italic">{emptyMessage}</p>;
  }

  const items = content.split('\n').filter(item => item.trim() !== '');

  if (items.length === 0) {
    return <p className="text-muted-foreground italic">{emptyMessage}</p>;
  }

  return (
    <div className="space-y-3">
      {items.map((item, index) => {
        const dateMatch = item.match(/\(([^)]+)\)$/);
        let mainText = item;
        let dateRange = dateMatch ? dateMatch[1] : null;
        
        if (dateMatch) {
          mainText = item.replace(/\s*\([^)]+\)$/, '');
        }

        const parts = mainText.split(' at ');
        const role = parts[0]?.trim();
        const organization = parts[1]?.trim();

        return (
          <div key={index} className="p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                <Icon className="w-4 h-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-foreground text-sm">{role || mainText}</p>
                {organization && <p className="text-xs text-muted-foreground mt-1">{organization}</p>}
                {dateRange && (
                  <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {dateRange}
                  </p>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Helper for funding items
const FundingList = ({ content, emptyMessage }) => {
  if (!content || content.trim() === '') {
    return <p className="text-muted-foreground italic">{emptyMessage}</p>;
  }

  const items = content.split('\n').filter(item => item.trim() !== '');

  if (items.length === 0) {
    return <p className="text-muted-foreground italic">{emptyMessage}</p>;
  }

  return (
    <div className="space-y-3">
      {items.map((item, index) => {
        const funderMatch = item.match(/\(funded by ([^)]+)\)/);
        let title = item;
        let funder = funderMatch ? funderMatch[1] : null;
        
        if (funderMatch) {
          title = item.replace(/\s*\(funded by [^)]+\)/, '');
        }

        return (
          <div key={index} className="p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-md bg-green-500/10 flex items-center justify-center shrink-0">
                <DollarSign className="w-4 h-4 text-green-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-foreground">{title}</p>
                {funder && (
                  <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                    <Building2 className="w-3 h-3" />
                    {funder}
                  </p>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default function ExpertDashboard() {
  const navigate = useNavigate();
  const { user, isAuthenticated, isExpert, logout, loading: authLoading, token, API } = useAuth();

  useEffect(() => {
    if (authLoading) return;

    if (!isAuthenticated) {
      navigate('/expert/signup');
      return;
    }
    
    if (!isExpert) {
      navigate('/');
      return;
    }
  }, [isAuthenticated, isExpert, navigate, authLoading]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (authLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'EX';
  };

  // Count items for badges
  const countItems = (content) => {
    if (!content) return 0;
    return content.split('\n').filter(item => item.trim() !== '').length;
  };

  const employmentCount = countItems(user.employment);
  const educationCount = countItems(user.education);
  const fundingCount = countItems(user.funding);
  const featuredCount = countItems(user.featured_works);
  const worksCount = countItems(user.works);
  const activitiesCount = countItems(user.professional_activities);

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Navigation */}
      <nav className="nav-header">
        <div className="container-main py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md gradient-primary flex items-center justify-center">
              <Users className="w-5 h-5 text-white" />
            </div>
            <div className="flex flex-col leading-none">
              <span className="text-xl font-semibold tracking-tight">CollabHive</span>
              <span className="text-[10px] text-muted-foreground mt-0.5">Powered by PM</span>
            </div>
          </Link>
          
          <div className="flex items-center gap-2 sm:gap-4">
            <Button
              variant="outline"
              size="sm"
              className="gap-2 sm:size-default"
              onClick={async () => {
                try {
                  const res = await fetch(`${API}/support/calendly`, { headers: { Authorization: `Bearer ${token}` } });
                  const data = await res.json();
                  const url = data?.calendly_url;
                  if (url) {
                    window.open(url, '_blank', 'noopener,noreferrer');
                  } else {
                    toast.error('Support link unavailable right now.');
                  }
                } catch (e) {
                  toast.error('Could not load the support link. Please try again.');
                }
              }}
              data-testid="expert-support-btn"
              title="Book a support call with the CollabHive admin"
            >
              <LifeBuoy className="w-4 h-4" />
              <span className="hidden sm:inline">Support</span>
            </Button>
            <Link to="/expert/complete-profile">
              <Button variant="outline" size="sm" className="gap-2 sm:size-default">
                <Edit className="w-4 h-4" />
                <span className="hidden sm:inline">Edit Profile</span>
              </Button>
            </Link>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="gap-2 sm:size-default" data-testid="logout-btn">
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </nav>

      <div className="container-main py-6 sm:py-10">
        <Tabs defaultValue="projects" className="w-full">
          <TabsList className="grid grid-cols-4 w-full max-w-2xl mb-6 h-auto">
            <TabsTrigger value="projects" className="gap-1 sm:gap-2 text-xs sm:text-sm py-2 flex-col sm:flex-row" data-testid="tab-projects">
              <Briefcase className="w-4 h-4" />
              <span>Projects</span>
            </TabsTrigger>
            <TabsTrigger value="opportunities" className="gap-1 sm:gap-2 text-xs sm:text-sm py-2 flex-col sm:flex-row" data-testid="tab-opportunities">
              <Megaphone className="w-4 h-4" />
              <span>Opportunities</span>
            </TabsTrigger>
            <TabsTrigger value="finance" className="gap-1 sm:gap-2 text-xs sm:text-sm py-2 flex-col sm:flex-row" data-testid="tab-finance">
              <Wallet className="w-4 h-4" />
              <span>Finance</span>
            </TabsTrigger>
            <TabsTrigger value="profile" className="gap-1 sm:gap-2 text-xs sm:text-sm py-2 flex-col sm:flex-row" data-testid="tab-profile">
              <UserCircle className="w-4 h-4" />
              <span>Profile</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="projects" className="mt-0">
            <Card className="animate-fade-in">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Briefcase className="w-5 h-5 text-primary" />
                  Industry Projects Matching Your Expertise
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ExpertAvailableProjects />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="opportunities" className="mt-0">
            <Card className="animate-fade-in">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Megaphone className="w-5 h-5 text-primary" />
                  R&amp;D Opportunities for You
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ExpertOpportunities />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="finance" className="mt-0">
            <Card className="animate-fade-in">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Wallet className="w-5 h-5 text-primary" />
                  Your Earnings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <FinanceSummary API={API} token={token} defaultRole="expert" />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="profile" className="mt-0">
            <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Left Column: Profile Summary */}
          <div className="space-y-6">
            <Card className="animate-fade-in">
              <CardContent className="pt-6">
                <div className="text-center">
                  <Avatar className="w-24 h-24 mx-auto mb-4">
                    <AvatarImage src={user.photo_url} alt={user.name} />
                    <AvatarFallback className="text-2xl bg-primary/10 text-primary">
                      {getInitials(user.name)}
                    </AvatarFallback>
                  </Avatar>
                  
                  <h2 className="text-xl font-semibold mb-1">{user.name}</h2>
                  <p className="text-muted-foreground text-sm mb-4">{user.email}</p>
                  
                  <Badge 
                    className={`qualification-badge ${user.is_approved ? 'approved' : 'pending'}`}
                    data-testid="approval-status"
                  >
                    {user.is_approved ? (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        Approved - Visible to Industry
                      </>
                    ) : (
                      <>
                        <AlertCircle className="w-4 h-4" />
                        Pending Approval
                      </>
                    )}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {/* Qualification Status */}
            <Card className="animate-fade-in stagger-1">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Qualification Status</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm">Lab Access</span>
                  {user.has_lab_access ? (
                    <CheckCircle className="w-5 h-5 text-primary" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-muted-foreground" />
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Expert Connections</span>
                  {user.connected_with_experts ? (
                    <CheckCircle className="w-5 h-5 text-primary" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-muted-foreground" />
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Industry Experience</span>
                  {user.worked_with_industry ? (
                    <CheckCircle className="w-5 h-5 text-primary" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-muted-foreground" />
                  )}
                </div>
                
                {!user.is_approved && (
                  <div className="pt-4 border-t">
                    <p className="text-xs text-muted-foreground">
                      All three criteria must be &quot;Yes&quot; to be visible to industry users.
                    </p>
                    <Link to="/expert/complete-profile">
                      <Button size="sm" className="w-full mt-3">
                        Update Qualifications
                      </Button>
                    </Link>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* ORCID */}
            {user.orcid_id && (
              <Card className="animate-fade-in stagger-2">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                      <ExternalLink className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">ORCID ID</p>
                      <a 
                        href={`https://orcid.org/${user.orcid_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:underline"
                      >
                        {user.orcid_id}
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Support — direct line to CollabHive admin */}
            <Card className="animate-fade-in stagger-3 border-primary/30" data-testid="expert-support-card">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                    <LifeBuoy className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold">Need help?</p>
                    <p className="text-xs text-muted-foreground">Book a 1:1 call with the CollabHive admin.</p>
                  </div>
                </div>
                <Button
                  size="sm"
                  className="w-full gap-2"
                  onClick={async () => {
                    try {
                      const res = await fetch(`${API}/support/calendly`, { headers: { Authorization: `Bearer ${token}` } });
                      const data = await res.json();
                      if (data?.calendly_url) window.open(data.calendly_url, '_blank', 'noopener,noreferrer');
                      else toast.error('Support link unavailable right now.');
                    } catch { toast.error('Could not load the support link.'); }
                  }}
                  data-testid="expert-support-card-btn"
                >
                  <LifeBuoy className="w-4 h-4" />
                  Schedule a Support Call
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Right Column: Detailed Info */}
          <div className="lg:col-span-2">
            <Card className="animate-fade-in stagger-1">
              <Tabs defaultValue="bio" className="w-full">
                <CardHeader className="pb-0 overflow-hidden">
                  <TabsList className="grid grid-cols-3 sm:grid-cols-6 w-full h-auto">
                    <TabsTrigger value="bio" className="gap-2 text-xs sm:text-sm">
                      <BookOpen className="w-4 h-4 hidden sm:block" />
                      Bio
                    </TabsTrigger>
                    <TabsTrigger value="employment" className="gap-2 text-xs sm:text-sm">
                      <Briefcase className="w-4 h-4 hidden sm:block" />
                      Work
                    </TabsTrigger>
                    <TabsTrigger value="education" className="gap-2 text-xs sm:text-sm">
                      <GraduationCap className="w-4 h-4 hidden sm:block" />
                      Education
                    </TabsTrigger>
                    <TabsTrigger value="funding" className="gap-2 text-xs sm:text-sm">
                      <DollarSign className="w-4 h-4 hidden sm:block" />
                      Funding
                    </TabsTrigger>
                    <TabsTrigger value="featured" className="gap-2 text-xs sm:text-sm">
                      <Star className="w-4 h-4 hidden sm:block" />
                      Publications
                    </TabsTrigger>
                    <TabsTrigger value="works" className="gap-2 text-xs sm:text-sm">
                      <FileText className="w-4 h-4 hidden sm:block" />
                      Patents
                    </TabsTrigger>
                  </TabsList>
                </CardHeader>
                
                <CardContent className="pt-6">
                  <TabsContent value="bio" className="mt-0">
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                          <BookOpen className="w-5 h-5 text-primary" />
                          Biography
                        </h3>
                        {user.biography ? (
                          <div className="p-4 rounded-lg bg-muted/30 border">
                            <p className="text-muted-foreground leading-relaxed whitespace-pre-wrap">
                              {user.biography}
                            </p>
                          </div>
                        ) : (
                          <p className="text-muted-foreground italic">No biography added yet.</p>
                        )}
                      </div>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="employment" className="mt-0">
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                          <Briefcase className="w-5 h-5 text-primary" />
                          Work Experience
                        </h3>
                        <ExperienceList 
                          content={user.employment} 
                          icon={Building2}
                          emptyMessage="No employment information added."
                        />
                      </div>
                      
                      {user.professional_activities && (
                        <div className="pt-6 border-t">
                          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                            <Award className="w-5 h-5 text-primary" />
                            Professional Activities
                            {activitiesCount > 0 && (
                              <Badge variant="secondary" className="text-xs">{activitiesCount}</Badge>
                            )}
                          </h3>
                          <FormattedList 
                            content={user.professional_activities}
                            icon={Award}
                            emptyMessage="No professional activities listed."
                          />
                        </div>
                      )}
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="education" className="mt-0">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <GraduationCap className="w-5 h-5 text-primary" />
                      Education
                    </h3>
                    <ExperienceList 
                      content={user.education}
                      icon={GraduationCap}
                      emptyMessage="No education information added."
                    />
                  </TabsContent>
                  
                  <TabsContent value="funding" className="mt-0">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-primary" />
                      Funding &amp; Grants
                    </h3>
                    <FundingList 
                      content={user.funding}
                      emptyMessage="No funding information added."
                    />
                  </TabsContent>
                  
                  <TabsContent value="featured" className="mt-0">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <Star className="w-5 h-5 text-primary" />
                      Publications
                    </h3>
                    <PublicationList 
                      content={user.featured_works}
                      emptyMessage="No publications added."
                    />
                  </TabsContent>
                  
                  <TabsContent value="works" className="mt-0">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <BookMarked className="w-5 h-5 text-primary" />
                      Patents
                    </h3>
                    <PublicationList 
                      content={user.works}
                      emptyMessage="No patents added."
                    />
                  </TabsContent>
                </CardContent>
              </Tabs>
            </Card>
          </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
